import { isNil, isFinite, get, set, round, reduce, isEmpty } from 'lodash';
import turf from '@turf/turf';

/**
 * @typedef {object} FeatureCollection
 * @typedef {object} Feature
 * @typedef {object} LatLng
 * @typedef {object} Point
 * @typedef {object} LineString
 * @typedef {object} Geometry
 * @typedef {object} Polygon
 */

/**
 * Returns a Line string with the waypoints.
 *
 * @param {FeatureCollection<Point>} pointsCollectionFeature - GeoJSON FeatureCollection with the waypoints.
 * @returns {Feature<LineString>} A new GeoJSON Feature LineString with the waypoints.
 */
export const pointsCollectionFeatureToLineString = pointsCollectionFeature => {
  const coordinatesArray = turf.coordAll(pointsCollectionFeature);
  return turf.lineString(coordinatesArray, {});
};

/**
 * Returns a feature point built from a leaflet LatLng object.
 *
 * @param {LatLng} latlong - Represents a geographical point with a certain latitude and longitude.
 * @returns {Feature<Point>} A new GeoJSON Feature Point with the position given.
 */
export const latlong2featurePoint = latlong => {
  let coords;
  if (isNil(latlong.alt)) {
    coords = [latlong.lng, latlong.lat];
  } else {
    coords = [latlong.lng, latlong.lat, latlong.alt];
  }
  return turf.point(coords);
};

/**
 * Returns a simple object that represents a leaflet LatLng.
 *
 * @param {Feature<Point>} featurePoint - A feature point with a given position.
 * @returns {LatLng} A simple object representing a leaflet LatLng.
 * @throws Error if featurePoint is not a feature point.
 */
export const featurePoint2LatLng = featurePoint => {
  turf.featureOf(featurePoint, 'Point', 'featurePoint2LatLng');
  const latlng = {};
  const coords = turf.getCoord(featurePoint);
  if (isNil(coords[2])) {
    [latlng.lng, latlng.lat] = coords;
  } else {
    [latlng.lng, latlng.lat, latlng.alt] = coords;
  }
  return latlng;
};

/**
 * Gets the zoom from the properties of a geojson feature.
 *
 * @param {Feature} feature - A GeoJSON feature.
 * @returns {number|undefined} The zoom property stored in the feature properties
 * or undefined if it does not exist.
 */
export const featureGetZoom = feature => get(feature, 'properties.zoom');

/**
 * Sets the given zoom as a properties for the given feature.
 *
 * @param {Feature} feature - A GeoJSON feature @mutated with new zoom property.
 * @param {number} zoom - The new zoom.
 */
export const featureSetZoom = (feature, zoom) => {
  set(feature, 'properties.zoom', zoom);
};

/**
 * Gets the bbox of a geojson feature.
 *
 * @param {Feature} feature - A GeoJSON feature.
 * @returns {number[]|undefined} The bbox of the feature or undefined if it does not exist.
 */
export const featureGetBBox = feature => get(feature, 'bbox');

/**
 * Sets the given bbox for the given feature.
 *
 * @param {Feature} feature - A GeoJSON feature @mutated with new bbox.
 * @param {number} bbox - The new bbox.
 */
export const featureSetBBox = (feature, bbox) => {
  set(feature, 'bbox', bbox);
};

/**
 * Gets the longitude for a feature point.
 *
 * @param {Feature<Point>} featurePoint - A GeoJSON feature that contains a point geometry.
 * @returns {number|undefined} The longitude for that position or undefined if it does not exist.
 */
export const featurePointGetLongitude = featurePoint =>
  get(featurePoint, 'geometry.coordinates[0]');

/**
 * Gets the latitude for a feature point.
 *
 * @param {Feature<Point>} featurePoint - A GeoJSON feature that contains a point geometry.
 * @returns {number|undefined} The latitude for that position or undefined if it does not exist.
 */
export const featurePointGetLatitude = featurePoint => get(featurePoint, 'geometry.coordinates[1]');

/**
 * Gets the altitude for a feature point.
 *
 * @param {Feature<Point>} featurePoint - A GeoJSON feature that contains a point geometry.
 * @returns {number|undefined} The altitude for that position or undefined if it does not exist.
 */
export const featurePointGetAltitude = featurePoint => get(featurePoint, 'geometry.coordinates[2]');

/**
 * Sets the altitude for a feature point as the 3rd coordinate in the position.
 *
 * @param {Feature<Point>} featurePoint - A GeoJSON feature that contains a point geometry @mutated with altitude coord.
 * @param {number} newAltitude - A GeoJSON feature that contains a point geometry.
 */
export const featurePointSetAltitude = (featurePoint, newAltitude) => {
  featurePoint.geometry.coordinates[2] = newAltitude; // eslint-disable-line
};

/**
 * Returns the bearing from waypointFrom to waypointTo.
 * The angle measured in degrees from the north line (0 degrees).
 *
 * @param {Feature<Point>} waypointFrom - A GeoJSON Feature representing a point.
 * @param {Feature<Point>} waypointTo - A GeoJSON Feature representing a point.
 * @returns {number} The bearing from waypointFrom to waypointTo in degrees.
 * @throws Error if waypointFrom or waypointTo are not feature points
 * bearing in decimal degrees, between -180 and 180 degrees (positive clockwise).
 */
export const getBearingToPoint = (waypointFrom, waypointTo) => {
  turf.featureOf(waypointFrom, 'Point', 'getBearingToPoint');
  turf.featureOf(waypointTo, 'Point', 'getBearingToPoint');
  const coordsFrom = turf.getCoord(waypointFrom);
  const coordsTo = turf.getCoord(waypointTo);
  return turf.bearing(coordsFrom, coordsTo);
};

/**
 * Returns the distance in meters from waypointFrom to waypointTo.
 *
 * @param {Feature<Point>} waypointFrom - A GeoJSON Feature representing a point.
 * @param {Feature<Point>} waypointTo - A GeoJSON Feature representing a point.
 * @returns {number} The distance to waypointTo in meters.
 * @throws Error if waypointFrom or waypointTo are not feature points.
 */
export const getDistanceToPoint = (waypointFrom, waypointTo) => {
  turf.featureOf(waypointFrom, 'Point', 'getDistanceToPoint');
  turf.featureOf(waypointTo, 'Point', 'getDistanceToPoint');
  const coordsFrom = turf.getCoord(waypointFrom);
  const coordsTo = turf.getCoord(waypointTo);
  return turf.distance(coordsFrom, coordsTo, { units: 'kilometers' }) * 1000;
};

/**
 * Takes a Point and calculates the location of a destination point given a
 * distance in meters; and bearing in degrees.
 * This uses the Haversine formula to account for global curvature.
 *
 * @param {Feature<Point>} waypointFrom - A GeoJSON Feature representing a point.
 * @param {number} bearingTo - The bearing to the next waypoint in degrees [-180 , 180].
 * @param {number} distanceTo - The distance to the next waypoint in meters.
 * @returns {Feature<Point>} A waypoint Feature in the bearing direction at the distance specified.
 * @throws Error if waypointFrom is not a feature point.
 * @throws {TypeError} If bearing is not a number.
 * @throws {TypeError} If distance is not a number or a negative number.
 */
export const getDestinationPoint = (waypointFrom, bearingTo, distanceTo) => {
  turf.featureOf(waypointFrom, 'Point', 'getDestinationPoint');

  if (!isFinite(bearingTo)) {
    throw new TypeError('The bearing is not a valid number');
  } else if (!isFinite(distanceTo) || distanceTo < 0) {
    throw new TypeError('The distance is not a valid number');
  }

  const coordsFrom = turf.getCoord(waypointFrom);
  const destFeature = turf.destination(coordsFrom, distanceTo / 1000, bearingTo, {
    units: 'kilometers',
    properties: {}
  });

  return destFeature;
};

/**
 * Checks if the geojson given is inside.
 * Bbox minX, minY, maxX, maxY order.
 *
 * @param {Feature|Geometry|FeatureCollection<Point>} geojsonInside - GeoJSON Feature, Geometry or collection of Feature Points.
 * @param {number[]} bboxToCheck - Bbox to check operation.
 * @returns {boolean} True if the geojsonInside is completely within a polygon created from bboxToCheck.
 * @throws Error if the feature collection is not build only of points.
 */
export const isInsideBbox = (geojsonInside, bboxToCheck) => {
  const bboxPolygon = turf.bboxPolygon(bboxToCheck);
  if (geojsonInside.type === 'FeatureCollection') {
    turf.collectionOf(geojsonInside, 'Point', 'isInsideBbox');
    const lineInside = pointsCollectionFeatureToLineString(geojsonInside);
    return turf.booleanWithin(lineInside, bboxPolygon);
  }
  return turf.booleanWithin(geojsonInside, bboxPolygon);
};

/**
 * Given feature collection of points and a feature point, returns
 * the feature point in the collection which is the closest to the location point.
 *
 * @param {Feature<Point>} point - The feature point to look for.
 * @param {FeatureCollection<Point>} featureCollection - The feature collection to look for the point.
 * @returns {Feature<Point>} Returns the feature point in the collection which is the closest to the given location.
 */
export const getClosestFeaturePointInCollection = (point, featureCollection) => {
  const featureLine = pointsCollectionFeatureToLineString(featureCollection);
  const closestPoint = turf.nearestPointOnLine(featureLine, point, { units: 'kilometers' });
  return featureCollection.features[closestPoint.properties.index];
};

/**
 * Given a feature point and a side lenght return the bbox of the square
 * that point as center and a side of lengthsquareSide.
 *
 * @param {Feature<Point>} point - The feature point.
 * @param {number} [squareSide=4] - The length of the bbox sqaure side in kilometers.
 * @returns {number[]} Returns the bbox array of a square with center in point and side length squareSide.
 * @throws Error if point is not a feature point.
 */
export const returnBboxFromFeaturePoint = (point, squareSide = 4) => {
  turf.featureOf(point, 'Point', 'returnBboxFromFeaturePoint');
  const halfSide = squareSide / Math.sqrt(2);
  const northEastPoint = turf.destination(point, halfSide, 45, { units: 'kilometers' });
  const southWestPoint = turf.destination(point, halfSide, -135, { units: 'kilometers' });
  const bbox = turf.bbox(turf.featureCollection([northEastPoint, southWestPoint]));
  return bbox;
};

/**
 * Given a feature point and the altitudeMode a kml placemark is built including the properties.
 *
 * @param {Feature<Point>} point - The feature point.
 * @param {string} [altitudeMode='clampToGround'] - The altitude mode for the kml.
 * @returns {string} Return a kml formated string with the feature point.
 * @throws Error if point is not a feature point.
 */
export const featurePoint2KML = (point, altitudeMode = 'clampToGround') => {
  turf.featureOf(point, 'Point', 'featurePoint2KML');
  const kmlPoint = `<Point><altitudeMode>${altitudeMode}</altitudeMode><coordinates>${turf
    .getCoord(point)
    .join(',')}</coordinates></Point>`;
  let kmlProperties = '';
  if (!isNil(point.properties) && !isEmpty(point.properties)) {
    kmlProperties = reduce(
      point.properties,
      (result, value, key) => `${result}<Data name="${key}"><value>${value}</value></Data>`,
      ''
    );
  }
  return `<Placemark>${kmlPoint}<ExtendedData>${kmlProperties}</ExtendedData></Placemark>`;
};

/**
 * Given a feature line and the altitudeMode a kml placemark is built including the properties.
 *
 * @param {Feature<LineString>} line - The feature line.
 * @param {string} [altitudeMode='clampToGround'] - The altitude mode for the kml.
 * @returns {string} Return a kml formatted string with the feature line.
 * @throws Error if line is not a feature linestring.
 */
export const featureLinestring2KML = (line, altitudeMode = 'clampToGround') => {
  turf.featureOf(line, 'LineString', 'featureLinestring2KML');
  const kmlCoords = turf.coordReduce(
    line,
    (previousValue, currentCoord) => {
      if (previousValue === '') {
        return currentCoord.join(',');
      }
      return `${previousValue} ${currentCoord.join(',')}`;
    },
    ''
  );
  const kmlLinestring = `<LineString><altitudeMode>${altitudeMode}</altitudeMode><coordinates>${kmlCoords}</coordinates></LineString>`;
  let kmlProperties = '';
  if (!isNil(line.properties) && !isEmpty(line.properties)) {
    kmlProperties = reduce(
      line.properties,
      (result, value, key) => `${result}<Data name="${key}"><value>${value}</value></Data>`,
      ''
    );
  }
  return `<Placemark>${kmlLinestring}<ExtendedData>${kmlProperties}</ExtendedData></Placemark>`;
};

/**
 * Given a feature polygon and the altitudeMode a kml placemark is built including the properties.
 *
 * @param {Feature<Polygon>} polygon - The feature polygon.
 * @param {string} [altitudeMode='clampToGround'] - The altitude mode for the kml.
 * @returns {string} Return a kml formatted string with the feature polygon.
 * @throws Error if polygon is not a feature polygon.
 */
export const featurePolygon2KML = (polygon, altitudeMode = 'clampToGround') => {
  turf.featureOf(polygon, 'Polygon', 'featurePolygon2KML');
  const kmlCoords = turf.coordReduce(
    polygon,
    (previousValue, currentCoord) => {
      if (previousValue === '') {
        return currentCoord.join(',');
      }
      return `${previousValue} ${currentCoord.join(',')}`;
    },
    ''
  );
  const kmlPolygon = `<Polygon><altitudeMode>${altitudeMode}</altitudeMode><outerBoundaryIs><LinearRing><coordinates>${kmlCoords}</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
  let kmlProperties = '';
  if (!isNil(polygon.properties) && !isEmpty(polygon.properties)) {
    kmlProperties = reduce(
      polygon.properties,
      (result, value, key) => `${result}<Data name="${key}"><value>${value}</value></Data>`,
      ''
    );
  }
  return `<Placemark>${kmlPolygon}<ExtendedData>${kmlProperties}</ExtendedData></Placemark>`;
};

/**
 * Given a feature collection and the altitudeMode a kml placemark is built including the properties.
 *
 * @param {FeatureCollection<Point>} featureCollection - The feature collection of points.
 * @param {string} [altitudeMode='clampToGround'] - The altitude mode for the kml.
 * @returns {string} Return a kml formatted string with the feature collection.
 * @throws Error if featureCollection is not a feature collection of points.
 */
export const featureCollectionPoints2KML = (featureCollection, altitudeMode = 'clampToGround') => {
  turf.collectionOf(featureCollection, 'Point', 'featureCollectionPoints2KML');
  return turf.featureReduce(
    featureCollection,
    (previousValue, currentFeature) => {
      if (previousValue === '') {
        return featurePoint2KML(currentFeature, altitudeMode);
      }
      return previousValue + featurePoint2KML(currentFeature, altitudeMode);
    },
    ''
  );
};

/**
 * Takes in the current position and the desired position to calculate the slope that
 * the RPA is going to fly with when sent to those coordinates.
 *
 * @param {Feature<Point>} fromPoint - The from waypoint.
 * @param {Feature<Point>} toPoint - The to waypoint.
 * @returns {number} The resuting slope in a straight line.
 * @throws Error if fromPosition or toPosition are not feature points.
 */
export const getSlope = (fromPoint, toPoint) => {
  turf.featureOf(fromPoint, 'Point', 'getSlope');
  turf.featureOf(toPoint, 'Point', 'getSlope');
  const deltaHeight = turf.getCoord(toPoint)[2] - turf.getCoord(fromPoint)[2];
  const deltaDistance = getDistanceToPoint(fromPoint, toPoint);
  return round((deltaHeight / deltaDistance) * 100);
};

export default {
  pointsCollectionFeatureToLineString,
  latlong2featurePoint,
  featurePoint2LatLng,
  featureGetZoom,
  featureSetZoom,
  featurePointGetLongitude,
  featurePointGetLatitude,
  featurePointGetAltitude,
  featurePointSetAltitude,
  getBearingToPoint,
  getDistanceToPoint,
  getDestinationPoint,
  isInsideBbox,
  getClosestFeaturePointInCollection,
  returnBboxFromFeaturePoint,
  featurePoint2KML,
  featureLinestring2KML,
  featurePolygon2KML,
  featureCollectionPoints2KML,
  getSlope
};
